import { formatCurrency, formatDate } from "@/lib/utils/formatters";
import { MEAL_PLANS } from "@/types/rooms";
import type {
  PriceBreakdown as PriceBreakdownType,
  SelectedAddon,
} from "@/types/booking";

interface PriceBreakdownProps {
  pricing: PriceBreakdownType;
  roomTypeName: string;
  checkIn: string;
  checkOut: string;
  addons?: SelectedAddon[];
  addonsTotal?: number;
}

export default function PriceBreakdown({
  pricing,
  roomTypeName,
  checkIn,
  checkOut,
  addons = [],
  addonsTotal = 0,
}: PriceBreakdownProps) {
  const grandTotal = pricing.total + addonsTotal;

  const mealPlanMeta = pricing.mealPlan
    ? MEAL_PLANS.find((p) => p.key === pricing.mealPlan)
    : null;

  return (
    <div className="rounded-xl border border-metro-cream-100 bg-metro-cream p-5 space-y-3">
      <h3 className="font-medium text-metro-green-950 text-sm">
        Price Summary
      </h3>

      <div className="text-xs text-metro-ink-subtle space-y-0.5">
        <p className="font-medium text-metro-ink-muted">{roomTypeName}</p>
        <p>
          {formatDate(checkIn, "dd MMM yyyy")} →{" "}
          {formatDate(checkOut, "dd MMM yyyy")}
        </p>
        {mealPlanMeta && (
          <p className="mt-1 inline-flex items-center gap-1 rounded-full bg-metro-green-100 px-2 py-0.5 text-[10px] font-medium text-metro-green-800">
            {mealPlanMeta.key} — {mealPlanMeta.description}
          </p>
        )}
      </div>

      <div className="border-t border-metro-cream-100 pt-3 space-y-2 text-sm">
        <div className="flex justify-between text-metro-ink-muted">
          <span>
            {formatCurrency(pricing.basePrice)} × {pricing.nights}{" "}
            {pricing.nights === 1 ? "night" : "nights"}
          </span>
          <span>{formatCurrency(pricing.subtotal)}</span>
        </div>

        <div className="flex justify-between text-metro-ink-muted">
          <span>GST ({pricing.gstRate}%)</span>
          <span>{formatCurrency(pricing.gstAmount)}</span>
        </div>

        {addons.length > 0 && (
          <div className="border-t border-metro-cream-100 pt-2 space-y-1.5">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-metro-ink-subtle">
              Add-ons
            </p>
            {addons.map((a) => (
              <div
                key={a.addonId}
                className="flex justify-between text-metro-ink-muted"
              >
                <span className="truncate pr-2">
                  {a.name}
                  {a.quantity > 1 && (
                    <span className="ml-1 text-[11px]">× {a.quantity}</span>
                  )}
                </span>
                <span className="shrink-0">{formatCurrency(a.lineTotal)}</span>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-between border-t border-metro-cream-100 pt-2 font-semibold text-metro-green-950">
          <span>Total</span>
          <span>{formatCurrency(grandTotal)}</span>
        </div>
      </div>
    </div>
  );
}